import { ChangeEvent, DragEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Ship } from "../App/Ship"
import { GetApp } from "../App"
import { GetShipContext } from "../App/Context"
import { useEditState } from "../App/useEditState"
import { useOverlay } from "../App/useOverlay"
import { EditGroupData } from "../Components/EditGroup"
import { useEditableList } from "../Components/useEditableList"
import { TabBar } from "../Components/TabBar"
import { IShip, IShipData, IShipDataAnchor, IShipDataEngine, IShipDataGenerator, IShipDataInsurance, IShipDataOtherEquipment, IShipDataRigElement, IShipDataSail, IShipDataSteeringSystem, IShipDataTank, IShipVesselType, INoteAttachment, UserApi } from "../Types"
import { DevicesMap, ShipDataContext, ShipDataState, ShipDataViewData, VesselTypeOptions } from "./ShipData/Context"
import { ShipDataGeneralView } from "./ShipData/GeneralView"
import { ShipDataDetailsView } from "./ShipData/DetailsView"
import { ShipDataTechnicView } from "./ShipData/TechnicView"
import { ShipDataSailingView } from "./ShipData/SailingView"
import { ShipDataEquipmentView } from "./ShipData/EquipmentView"
import { ShipDataUsersView } from "./ShipData/UsersView"
import { GetShipView } from "./ShipView"
import { logger } from "../App/Logger"
import { newUUID } from "../App/Identifier"
import { useSettingsWriters } from "../Data"
import { useShipAttachmentUploader } from "./ShipData/attachmentWriters"

export { GetShipData, ShipDataContext, ShipDataViewData } from "./ShipData/Context"
export type { ShipDataState } from "./ShipData/Context"

interface ShipDataViewProps {
  attachments?: INoteAttachment[];
  data: IShipData;
  defaultSelection?: string;
  ship: IShip;
}

export function ShipDataView({ attachments, data, defaultSelection = "general", ship }: ShipDataViewProps) {
  const app = GetApp();
  const { shipCloudEditable } = GetShipView();
  const editable = app.editable || shipCloudEditable;
  const t = app.getT("ShipDataView");
  const _ts = app.getT("ShipSettingsView");
  const vesselTypeOptions: VesselTypeOptions = {
    [IShipVesselType.motor]: ["motor-boat", "motor-cat", "motor-tri", "motor-yacht", "motor-superyacht"],
    [IShipVesselType.sailing]: ["sailing-sloop", "sailing-classic", "sailing-ketch", "sailing-longkeeler", "sailing-cat", "sailing-tri"],
  };
  const [selectedShipType, setSelectedShipType] = useState<string>(data.shipType || vesselTypeOptions[ship.vesselType]?.[0] || "motor-boat");
  const shipType = useMemo(() => {
    if (ship.vesselType === undefined) {
      return undefined;
    }
    const options = vesselTypeOptions[ship.vesselType] || [];
    return options.find((type) => type === selectedShipType) || options[0];
  }, [ship, selectedShipType]);

  const newId = newUUID;

  const [showAttachment, setShowAttachment] = useState<number>(0);
  const [uploadedAttachments, setUploadedAttachments] = useState<INoteAttachment[]>([]);
  const displayedAttachments = useMemo(() => {
    const persisted = attachments || [];
    const persistedIds = new Set(persisted.map((attachment) => attachment.id));
    return [...persisted, ...uploadedAttachments.filter((attachment) => !persistedIds.has(attachment.id))];
  }, [attachments, uploadedAttachments]);
  const engineList = useEditableList<IShipDataEngine>(data.engines || [], () => ({ id: newId() } as IShipDataEngine));
  const generatorList = useEditableList<IShipDataGenerator>(data.generators || [], () => ({ id: newId(), generatorType: "", manufacturer: "", model: "", serialNumber: "", power: undefined, fuelType: "", comment: "" }));
  const insuranceList = useEditableList<IShipDataInsurance>(data.insurances || [], () => ({ id: newId(), insuranceType: "", insuranceCompany: "", insuranceNumber: "" }));
  const sailList = useEditableList<IShipDataSail>(data.sails || [], () => ({ id: newId(), sail: "", sailType: "", size: undefined, manufacturer: "", comment: "" }));
  const tankList = useEditableList<IShipDataTank>(data.tanks || [], () => ({ id: newId(), tankType: "", capacity: undefined, placeInstallation: "", comment: "" }));
  const anchorList = useEditableList<IShipDataAnchor>(data.anchors || [], () => ({ id: newId(), anchorType: "", weight: undefined, placeInstallation: "", chain: "", chainLead: "", windlass: "", windlassDrive: "", windlassManufacturer: "" }));
  const steeringSystemList = useEditableList<IShipDataSteeringSystem>(data.steeringSystems || [], () => ({ id: newId(), steeringType: "", power: undefined, manufacturer: "", model: "", comment: "" }));
  const otherEquipmentList = useEditableList<IShipDataOtherEquipment>(data.otherEquipments || [], () => ({ id: newId(), equipment: "" }));
  const rigElementList = useEditableList<IShipDataRigElement>(data.rigElements || [], () => ({ id: newId(), rigElement: "", manufacturer: "", model: "", size: undefined, serialNumber: "", comment: "", placeInstallation: "" }));
  const formEditable = app.editable;

  const [_addShipUser, _setAddShipUser] = useState<boolean>(false);
  const [newUserOverlay, , setNewUserOverlay] = useOverlay<boolean, undefined>(false);
  const { apiUser } = GetShipContext();
  const shipUsersManageable = new UserApi(app.apiClient).shipUsers.create({ params: { shipId: ship.id }, body: {} }).available(apiUser);
  const settingsWriters = useSettingsWriters(ship.id);
  const uploadShipAttachment = useShipAttachmentUploader(ship.id);

  useEffect(() => {
    if (data.engines) {
      engineList.setItems(data.engines);
    }
    if (data.generators) {
      generatorList.setItems(data.generators);
    }
    if (data.insurances) {
      insuranceList.setItems(data.insurances);
    }
    if (data.sails) {
      sailList.setItems(data.sails);
    }
    if (data.tanks) {
      tankList.setItems(data.tanks);
    }
    if (data.anchors) {
      anchorList.setItems(data.anchors);
    }
    if (data.steeringSystems) {
      steeringSystemList.setItems(data.steeringSystems);
    }
    if (data.otherEquipments) {
      otherEquipmentList.setItems(data.otherEquipments);
    }
    if (data.rigElements) {
      rigElementList.setItems(data.rigElements);
    }
  }, [data]);

  const shipUserCrewRoleOptions = useMemo(() => {
    const t = app.getT("ShipSettingsView");
    return ShipDataViewData.crewRoleOptions(t);
  }, []);

  const shipUserLevelOptions = useMemo(() => {
    const t = app.getT("ShipSettingsView");
    return ShipDataViewData.userLevelOptions(t);
  }, []);

  const saveData = useCallback(async (values: EditGroupData, namePrefix?: string) => {
    logger.debug("save", namePrefix, ship.id);
    await settingsWriters.updateShipData(values);
  }, [settingsWriters, ship]);

  const [edited, onEdited, resetEdited] = useEditState();
  const save = useCallback(async (_data: FormData, values: EditGroupData) => {
    await saveData(values);
    resetEdited();
  }, []);

  // Insert photo upload handling (single image only)
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveAttachments = useCallback(async (_data: FormData, _values: any) => {
    let nextImage = displayedAttachments.length;
    for (const file of files) {
      const result = await uploadShipAttachment(file);
      const attachment = result.attachment;
      if (attachment) {
        setUploadedAttachments((current) => [...current.filter((item) => item.id !== attachment.id), attachment]);
        nextImage += 1;
      }
    }
    setFiles([]);
    setShowAttachment(Math.max(0, nextImage - 1));
  }, [files, displayedAttachments, uploadShipAttachment]);

  const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      // Only take the first file
      setFiles([droppedFiles[0]]);
    }
  }, []);

  const handleSelect = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files && event.target.files.length > 0) {
      // Only take the first file
      setFiles([event.target.files[0]]);
    }
  }, []);

  const removeAttachment = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setFiles([]);
  }, []);

  // Definition for devices

  const deviceTypes = useMemo(() => ["electric", "nautical", "safety"], []);
  const [devices, setDevices] = useState<DevicesMap>((data.devices || {}) as DevicesMap);

  const addDevice = useCallback((type: string) => {
    const newDevices = { ...devices, [type]: [...(devices[type] || []), { id: newId(), deviceType: "" }] };
    setDevices(newDevices);
  }, [devices, setDevices]);
  const removeDevice = useCallback((type: string, index: number) => {
    const newDevices = { ...devices, [type]: [...devices[type].filter((_, i) => i != index)] };
    setDevices(newDevices);
  }, [devices, setDevices]);

  // Set the selctions lists for units

  const state: ShipDataState = {
    data, ship, attachments: displayedAttachments,
    editable, formEditable,
    edited, onEdited, save, saveData,
    newId,
    files, saveAttachments, handleDrop, handleSelect, removeAttachment, inputRef, showAttachment, setShowAttachment,
    editInsurance: insuranceList,
    editGenerator: generatorList,
    editSail: sailList,
    editTank: tankList,
    editAnchor: anchorList,
    editSteeringSystem: steeringSystemList,
    editOtherEquipment: otherEquipmentList,
    editRigElement: rigElementList,
    editEngine: engineList,
    devices, deviceTypes, addDevice, removeDevice,
    selectedShipType, setSelectedShipType, shipType, vesselTypeOptions,
    shipUserCrewRoleOptions, shipUserLevelOptions, newUserOverlay, setNewUserOverlay,
    shipUsersManageable,
  };

  return (
    <ShipDataContext.Provider value={state}>
      <div className="ShipDataView">

        <TabBar defaultSelection={defaultSelection} tabs={[
          { name: "general", title: t("tab_general"), render: () => <ShipDataGeneralView /> },
          { name: "details", title: t("tab_details"), render: () => <ShipDataDetailsView /> },
          { name: "technic", title: t("tab_technic"), render: () => <ShipDataTechnicView /> },
          { name: "sailing", hide: !Ship.isSailingVessel(ship), title: t("tab_sailing"), render: () => <ShipDataSailingView /> },
          { name: "equipment", title: t("tab_equipment"), render: () => <ShipDataEquipmentView /> },
          { name: "tab_ship_users", title: t("tab_ship_user"), hide: !shipUsersManageable, render: () => <ShipDataUsersView /> },
        ]} selectedCallback={(_value?: string) => {
          resetEdited();
        }}
        />

      </div>
    </ShipDataContext.Provider>
  );
}
